import React, { FC, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { UserStatus } from 'api/models';
import { Menu } from 'components/atoms/dropdown/menu';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { Member } from 'data/members/api';
import { useBillingFeatureFlag, useIsAccountActive } from 'hooks/use-account';
import { AccountFeature, useAccountFeature } from 'hooks/use-account-feature';
import { useHasAccess } from 'hooks/use-has-access';
import { useUser } from 'hooks/use-user';
import {
  useAvailableLicensesQuery,
  useReactivateMemberEstimateMutation,
} from 'queries/billing';
import { fullName } from 'utils/full-name';
import { ReactivationBillingWrapper } from './billing-wrapper';
import { useResendAction, useToggleActivation } from './use-actions';

export const TeamMembersMoreActionsMenu: FC<{
  member: Member;
  showMemberDetail: (member: Member) => void;
  openCostRateManager: (id: number) => void;
}> = ({ member, showMemberDetail, openCostRateManager }) => {
  const isBillingEnabled = useBillingFeatureFlag();
  const [showPaymentFlow, setShowPaymentFlow] = useState(false);

  const availableLicenses = useAvailableLicensesQuery();

  const { user, permissionSet } = member;
  const { user: currentUser } = useUser();
  const { userStatus, userId } = user;
  const userName = fullName(user);
  const isInvited = userStatus === UserStatus.Invited;

  const isActive = userStatus === UserStatus.Active || isInvited;
  const isSameUser = currentUser.userId === userId;

  const [isFinancialsEnabled] = useAccountFeature(AccountFeature.Financials);
  const [accountability] = useAccountFeature(AccountFeature.Accountability);
  const { hasAccess: canReadCostRates } = useHasAccess({
    permissions: ['READ'],
    resource: 'costRate',
  });
  const canViewCostRates =
    accountability && isFinancialsEnabled && canReadCostRates;

  const {
    onToggleActivation,
    isReactivating,
    isReactivationSuccess,
    isReactivationError,
  } = useToggleActivation(userId, isActive, userName);

  const isAccountActive = useIsAccountActive();

  const onResendInvitation = useResendAction(user, permissionSet);

  const _onToggleActivation = useCallback(() => {
    /**
     * check for available licenses for reactivation.
     */
    if (isActive || !isBillingEnabled || availableLicenses > 0) {
      onToggleActivation();

      return;
    }
    setShowPaymentFlow(true);
  }, [availableLicenses, isActive, isBillingEnabled, onToggleActivation]);

  return (
    <>
      <MoreActionsDropdown
        menu={
          <Menu>
            <Menu.Item onClick={() => showMemberDetail(member)}>
              <FormattedMessage id="view_detail" />
            </Menu.Item>

            {isInvited && !isSameUser ? (
              <Menu.Item onClick={onResendInvitation}>
                <FormattedMessage id="resend" />
              </Menu.Item>
            ) : null}

            {canViewCostRates && (
              <Menu.Item
                key="manageCostRate"
                onClick={() => {
                  openCostRateManager(member.user.userId);
                }}
              >
                <FormattedMessage id="settings.cost_rates.title" />
              </Menu.Item>
            )}

            {!isSameUser ? (
              <Menu.Item onClick={_onToggleActivation} danger={isActive}>
                {isActive ? (
                  <FormattedMessage id="deactivate" />
                ) : (
                  isAccountActive && <FormattedMessage id="reactivate" />
                )}
              </Menu.Item>
            ) : null}
          </Menu>
        }
      />
      {isBillingEnabled && (
        <ReactivationBillingWrapper
          visible={showPaymentFlow}
          user={user}
          onSubmit={onToggleActivation}
          isProcessing={isReactivating}
          isSuccess={isReactivationSuccess}
          isError={isReactivationError}
          onCancel={() => setShowPaymentFlow(false)}
          estimateMutation={useReactivateMemberEstimateMutation}
        />
      )}
    </>
  );
};
